import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { apiUrl } from "../../config/api";
import Button from "../UI/Button";

export default function DonateSuccess() {
  const [status, setStatus] = useState("Checking...");
  const [details, setDetails] = useState(null);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  const completedToastShown = useRef(false);
  const failedToastShown = useRef(false);
  const downloadReceiptRef = useRef(null);

  const paymentIntentId = useMemo(() => {
    const sp = new URLSearchParams(window.location.search);
    const fromQuery = sp.get("payment_intent_id");
    if (fromQuery) return fromQuery;
    return localStorage.getItem("last_payment_intent_id") || "";
  }, []);

  useEffect(() => {
    let alive = true;
    let timer = null;

    const poll = async () => {
      try {
        setError("");

        if (!paymentIntentId) {
          setStatus("Submitted");
          setError("Missing payment reference. Please check the dashboard.");
          return;
        }

        try {
          await fetch(apiUrl("/api/donations/sync-status"), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              payment_intent_id: paymentIntentId,
            }),
          });
        } catch (e) {
          console.error("sync-status failed:", e);
        }

        const res = await fetch(
          apiUrl(
            `/api/donations/public-status?payment_intent_id=${encodeURIComponent(
              paymentIntentId
            )}`
          )
        );

        const data = await res.json().catch(() => ({}));
        if (!alive) return;

        if (!res.ok) {
          setStatus("Submitted");
          setError(data.message || "Unable to verify payment yet.");
          return;
        }

        setDetails(data);

        const s = String(data.status || "").toLowerCase();

        if (s === "completed") {
          setStatus("Completed ✅");

          if (!completedToastShown.current) {
            toast.success("Donation confirmed successfully!");
            completedToastShown.current = true;
          }

          localStorage.removeItem("last_payment_intent_id");

          if (timer) {
            clearInterval(timer);
            timer = null;
          }
          return;
        }

        if (s === "failed") {
          setStatus("Failed ❌");

          if (!failedToastShown.current) {
            toast.error("Donation failed.");
            failedToastShown.current = true;
          }

          localStorage.removeItem("last_payment_intent_id");

          if (timer) {
            clearInterval(timer);
            timer = null;
          }
          return;
        }

        setStatus("Pending ⏳");
      } catch (e) {
        if (!alive) return;
        setStatus("Submitted");
        setError("Network issue while verifying. Please refresh.");
      }
    };

    poll();
    timer = setInterval(poll, 3000);

    return () => {
      alive = false;
      if (timer) clearInterval(timer);
    };
  }, [paymentIntentId]);

  const isCompleted = String(details?.status || "").toLowerCase() === "completed";

  const badge = status.includes("Completed")
    ? "border-emerald-200/60 dark:border-emerald-500/20 bg-emerald-50/70 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-200"
    : status.includes("Failed")
    ? "border-red-200/60 dark:border-red-500/20 bg-red-50/70 dark:bg-red-500/10 text-red-700 dark:text-red-200"
    : "border-yellow-200/60 dark:border-yellow-500/20 bg-yellow-50/70 dark:bg-yellow-500/10 text-yellow-800 dark:text-yellow-200";

  const icon = status.includes("Completed")
    ? "✅"
    : status.includes("Failed")
    ? "❌"
    : "⏳";

  const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const receiptNumber = useMemo(() => {
    if (!details?.payment_intent_id) return "VOH-REC-N/A";
    const cleaned = String(details.payment_intent_id)
      .replace(/[^a-zA-Z0-9]/g, "")
      .slice(-10)
      .toUpperCase();
    return `VOH-REC-${cleaned}`;
  }, [details]);

  const receiptDate = useMemo(() => {
    const raw =
      details?.completed_at ||
      details?.updated_at ||
      details?.created_at ||
      new Date().toISOString();

    const d = new Date(raw);
    return Number.isNaN(d.getTime())
      ? new Date().toLocaleString()
      : d.toLocaleString();
  }, [details]);

  const handlePrint = () => {
    if (!details) {
      toast.error("No receipt details available yet.");
      return;
    }

    if (!isCompleted) {
      toast.error("Receipt can only be printed when donation is completed.");
      return;
    }

    window.print();
  };

  const handleDownloadReceipt = async () => {
    try {
      if (!details) {
        toast.error("No receipt details available yet.");
        return;
      }

      if (!isCompleted) {
        toast.error("Receipt can only be downloaded when donation is completed.");
        return;
      }

      if (!downloadReceiptRef.current) {
        toast.error("Receipt template not found.");
        return;
      }

      setDownloading(true);
      toast.loading("Preparing receipt PDF...", { id: "receipt-download" });

      const canvas = await html2canvas(downloadReceiptRef.current, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const margin = 10;
      const usableWidth = pdfWidth - margin * 2;

      const imgWidth = usableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = margin;

      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= pdfHeight - margin * 2;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
        heightLeft -= pdfHeight - margin * 2;
      }

      const safeReceiptNo = receiptNumber.replace(/[^A-Z0-9-]/gi, "_");
      pdf.save(`${safeReceiptNo}.pdf`);

      toast.success("Receipt downloaded successfully!", {
        id: "receipt-download",
      });
    } catch (err) {
      console.error("download receipt failed:", err);
      toast.error("Failed to download receipt.", { id: "receipt-download" });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <style>
        {`
          @media print {
              @page {
                size: auto;
                margin: 10mm;
              }

              html, body {
                width: 100%;
                height: auto;
                background: white !important;
              }

              body * {
                visibility: hidden;
              }

              #print-receipt,
              #print-receipt * {
                visibility: visible;
              }

              #print-receipt {
                position: absolute;
                left: 0;
                top: 0;
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: flex-start;
              }

              .print-card {
                width: 180mm;        /* fits A4 and short bond */
                max-width: 95%;
                margin: 0 auto;
                padding: 20mm;
                box-shadow: none !important;
                border: 1px solid #ccc;
                page-break-inside: avoid;
              }

              .no-print {
                display: none !important;
              }

            }
        `}
      </style>

      <div className="min-h-screen relative overflow-hidden flex items-center justify-center bg-gray-50 dark:bg-gray-950 p-6">
        <div className="pointer-events-none absolute inset-0 no-print">
          <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-emerald-400/20 blur-3xl" />
          <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-blue-400/20 blur-3xl" />
        </div>

        <div className="relative w-full max-w-3xl">
          <div className="rounded-3xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-2xl p-6 sm:p-8 no-print">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${badge}`}
              >
                <span
                  className={`h-2 w-2 rounded-full ${
                    status.includes("Completed")
                      ? "bg-emerald-500"
                      : status.includes("Failed")
                      ? "bg-red-500"
                      : "bg-yellow-500"
                  }`}
                />
                {status}
              </div>

              <div className="text-xs text-gray-500 dark:text-gray-400">
                Ref: {paymentIntentId || "N/A"}
              </div>
            </div>

            <div className="mt-5 flex items-center justify-center">
              <div className="h-14 w-14 rounded-2xl bg-emerald-500/10 text-emerald-700 dark:text-emerald-200 flex items-center justify-center text-2xl">
                {icon}
              </div>
            </div>

            <h1 className="mt-4 text-center text-2xl sm:text-3xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100">
              Thank you for your donation!
            </h1>

            <p className="mt-2 text-center text-sm sm:text-base text-gray-700 dark:text-gray-200 leading-relaxed">
              {isCompleted
                ? "Your donation has been confirmed. You may now print or download the official receipt."
                : "We’re verifying your donation status automatically."}
            </p>

            {details && (
              <div className="mt-5 rounded-2xl border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-950 p-4">
                <div className="text-sm text-gray-700 dark:text-gray-200 space-y-2">
                  <div>
                    <span className="font-semibold">Donor:</span>{" "}
                    {details.donor_name || "Anonymous"}
                  </div>
                  <div>
                    <span className="font-semibold">Purpose:</span>{" "}
                    {details.purpose || "-"}
                  </div>
                  <div>
                    <span className="font-semibold">Amount:</span>{" "}
                    {details.currency || "PHP"} {formatAmount(details.amount)}
                  </div>
                  <div>
                    <span className="font-semibold">Status:</span>{" "}
                    {details.status || "-"}
                  </div>
                  <div className="break-all">
                    <span className="font-semibold">Payment Intent ID:</span>{" "}
                    {details.payment_intent_id || "-"}
                  </div>
                  <div>
                    <span className="font-semibold">Receipt No:</span>{" "}
                    {receiptNumber}
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 rounded-2xl border border-red-200 dark:border-red-900 bg-red-50/70 dark:bg-red-950/30 p-4 text-sm text-red-700 dark:text-red-200">
                {error}
              </div>
            )}

            <div className="mt-6 h-px w-full bg-gray-200/70 dark:bg-gray-800/70" />

            <div className="mt-5 grid grid-cols-1 sm:grid-cols-4 gap-3">
              <Button
                variant="outline"
                size="medium"
                className="w-full !rounded-2xl"
                onClick={() => {
                  toast("Refreshing status...");
                  window.location.reload();
                }}
              >
                Refresh
              </Button>

              <Button
                variant="primary"
                size="medium"
                className="w-full !rounded-2xl"
                onClick={handlePrint}
                disabled={!isCompleted}
              >
                Print Receipt
              </Button>

              <Button
                variant="primary"
                size="medium"
                className="w-full !rounded-2xl"
                onClick={handleDownloadReceipt}
                disabled={!isCompleted || downloading}
              >
                {downloading ? "Downloading..." : "Download Receipt"}
              </Button>

              <Button
                variant="primary"
                size="medium"
                className="w-full !rounded-2xl"
                onClick={() => {
                  window.location.href = "/donate";
                }}
              >
                Go Home
              </Button>
            </div>

            <div className="mt-5 text-center text-xs text-gray-500 dark:text-gray-400">
              You can close this tab safely.
            </div>
          </div>

          {/* PRINTABLE RECEIPT */}
          <div id="print-receipt" className="hidden print:block">
            <div className="print-card max-w-3xl mx-auto rounded-2xl border border-gray-300 bg-white p-8">
              <div className="flex items-start justify-between gap-6 border-b border-gray-300 pb-5 print-border">
                <div>
                  <h1 className="text-2xl font-extrabold tracking-wide text-gray-900 print-text">
                    VILLAGE OF HOPE
                  </h1>
                  <p className="mt-1 text-sm text-gray-600 print-subtext">
                    Web-Based Child Tracking and Development Monitoring
                  </p>
                  <p className="text-sm text-gray-600 print-subtext">
                    Official Donation Receipt
                  </p>
                </div>

                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest text-gray-500 print-subtext">
                    Receipt No.
                  </div>
                  <div className="text-sm font-bold text-gray-900 print-text">
                    {receiptNumber}
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 print-subtext">
                    Received From
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-900 print-text">
                    {details?.donor_name || "Anonymous"}
                  </p>
                </div>

                <div className="text-left sm:text-right">
                  <p className="text-xs uppercase tracking-wider text-gray-500 print-subtext">
                    Receipt Date
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-900 print-text">
                    {receiptDate}
                  </p>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-gray-200 p-5 print-border print-bg">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 print-subtext">
                      Donation Purpose
                    </p>
                    <p className="mt-1 font-medium text-gray-900 print-text">
                      {details?.purpose || "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 print-subtext">
                      Payment Status
                    </p>
                    <p className="mt-1 font-medium text-gray-900 print-text">
                      {details?.status || "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 print-subtext">
                      Currency
                    </p>
                    <p className="mt-1 font-medium text-gray-900 print-text">
                      {details?.currency || "PHP"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500 print-subtext">
                      Payment Reference
                    </p>
                    <p className="mt-1 font-medium text-gray-900 break-all print-text">
                      {details?.payment_intent_id || "-"}
                    </p>
                  </div>
                </div>

                <div className="mt-6 border-t border-dashed border-gray-300 pt-5 print-border">
                  <p className="text-xs uppercase tracking-widest text-gray-500 print-subtext">
                    Amount Received
                  </p>
                  <p className="mt-2 text-3xl font-extrabold text-gray-900 print-text">
                    {details?.currency || "PHP"} {formatAmount(details?.amount)}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <p className="text-sm leading-7 text-gray-700 print-subtext">
                  This certifies that the amount stated above was received as a
                  donation for the stated purpose. Thank you for supporting
                  Village of Hope and helping us continue our mission.
                </p>
              </div>

              <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-10">
                <div>
                  <div className="border-b border-gray-400 h-12 print-border" />
                  <p className="mt-2 text-sm font-semibold text-gray-900 print-text">
                    Authorized Signature
                  </p>
                  <p className="text-xs text-gray-500 print-subtext">
                    Village of Hope Representative
                  </p>
                </div>

                <div>
                  <div className="border-b border-gray-400 h-12 print-border" />
                  <p className="mt-2 text-sm font-semibold text-gray-900 print-text">
                    Donor Signature
                  </p>
                  <p className="text-xs text-gray-500 print-subtext">
                    Optional
                  </p>
                </div>
              </div>

              <div className="mt-10 border-t border-gray-300 pt-4 text-center print-border">
                <p className="text-xs text-gray-500 print-subtext">
                  Generated electronically by the Village of Hope Donation
                  System
                </p>
              </div>
            </div>
          </div>

          {/* HIDDEN DOWNLOAD TEMPLATE */}
          <div className="absolute left-[-99999px] top-0 opacity-0 pointer-events-none">
            <div
              ref={downloadReceiptRef}
              className="w-[900px] bg-white p-8 text-black border border-gray-300 rounded-2xl"
            >
              <div className="flex items-start justify-between gap-6 border-b border-gray-300 pb-5">
                <div>
                  <h1 className="text-2xl font-extrabold tracking-wide text-gray-900">
                    VILLAGE OF HOPE
                  </h1>
                  <p className="mt-1 text-sm text-gray-600">
                    Web-Based Child Tracking and Development Monitoring
                  </p>
                  <p className="text-sm text-gray-600">
                    Official Donation Receipt
                  </p>
                </div>

                <div className="text-right">
                  <div className="text-xs uppercase tracking-widest text-gray-500">
                    Receipt No.
                  </div>
                  <div className="text-sm font-bold text-gray-900">
                    {receiptNumber}
                  </div>
                </div>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500">
                    Received From
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-900">
                    {details?.donor_name || "Anonymous"}
                  </p>
                </div>

                <div className="text-right">
                  <p className="text-xs uppercase tracking-wider text-gray-500">
                    Receipt Date
                  </p>
                  <p className="mt-1 text-base font-semibold text-gray-900">
                    {receiptDate}
                  </p>
                </div>
              </div>

              <div className="mt-6 rounded-2xl border border-gray-200 p-5 bg-white">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500">
                      Donation Purpose
                    </p>
                    <p className="mt-1 font-medium text-gray-900">
                      {details?.purpose || "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500">
                      Payment Status
                    </p>
                    <p className="mt-1 font-medium text-gray-900">
                      {details?.status || "-"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500">
                      Currency
                    </p>
                    <p className="mt-1 font-medium text-gray-900">
                      {details?.currency || "PHP"}
                    </p>
                  </div>

                  <div>
                    <p className="text-xs uppercase tracking-wider text-gray-500">
                      Payment Reference
                    </p>
                    <p className="mt-1 font-medium text-gray-900 break-all">
                      {details?.payment_intent_id || "-"}
                    </p>
                  </div>
                </div>

                <div className="mt-6 border-t border-dashed border-gray-300 pt-5">
                  <p className="text-xs uppercase tracking-widest text-gray-500">
                    Amount Received
                  </p>
                  <p className="mt-2 text-3xl font-extrabold text-gray-900">
                    {details?.currency || "PHP"} {formatAmount(details?.amount)}
                  </p>
                </div>
              </div>

              <div className="mt-6">
                <p className="text-sm leading-7 text-gray-700">
                  This certifies that the amount stated above was received as a
                  donation for the stated purpose. Thank you for supporting
                  Village of Hope and helping us continue our mission.
                </p>
              </div>

              <div className="mt-12 grid grid-cols-2 gap-10">
                <div>
                  <div className="border-b border-gray-400 h-12" />
                  <p className="mt-2 text-sm font-semibold text-gray-900">
                    Authorized Signature
                  </p>
                  <p className="text-xs text-gray-500">
                    Village of Hope Representative
                  </p>
                </div>

                <div>
                  <div className="border-b border-gray-400 h-12" />
                  <p className="mt-2 text-sm font-semibold text-gray-900">
                    Donor Signature
                  </p>
                  <p className="text-xs text-gray-500">Optional</p>
                </div>
              </div>

              <div className="mt-10 border-t border-gray-300 pt-4 text-center">
                <p className="text-xs text-gray-500">
                  Generated electronically by the Village of Hope Donation
                  System
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}